import {
  AiCreditWalletSummaryResult,
  BillingSummaryResult,
  parseAiCreditWalletSummaryResult,
  parseBillingSummaryResult,
} from '@qkindex/contracts';
import {
  getAiCreditWalletSummary,
  getBillingSummary,
} from '../../firebase/functions';

interface Snapshot {
  userId?: string;
  billing?: BillingSummaryResult;
  wallet?: AiCreditWalletSummaryResult;
  loading: boolean;
  error?: unknown;
}
export function createBillingSummaryStore() {
  let snapshot: Snapshot = { loading: false };
  let generation = 0;
  let lastAttempt = 0;
  let pending: Promise<Snapshot | undefined> | undefined;
  const listeners = new Set<() => void>();
  const publish = (next: Snapshot) => {
    snapshot = next;
    listeners.forEach((listener) => listener());
  };
  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
    setAccount(userId?: string) {
      if (snapshot.userId === userId) return;
      generation++;
      pending = undefined;
      lastAttempt = 0;
      publish({ userId, loading: false });
    },
    refresh(userId: string, force = false): Promise<Snapshot | undefined> {
      if (snapshot.userId !== userId) return Promise.resolve(undefined);
      if (pending) return pending;
      if (!force && Date.now() - lastAttempt < 60_000)
        return Promise.resolve(snapshot);
      const requestGeneration = generation;
      lastAttempt = Date.now();
      // Keep failed-refresh state visible until a successful response replaces it.
      publish({ ...snapshot, loading: true });
      pending = (async () => {
        try {
          // Account initialization must precede wallet reads.
          const billing = parseBillingSummaryResult(
            (await getBillingSummary({})).data,
          );
          if (generation !== requestGeneration) return undefined;
          const wallet = parseAiCreditWalletSummaryResult(
            (await getAiCreditWalletSummary({})).data,
          );
          if (generation !== requestGeneration) return undefined;
          publish({ userId, billing, wallet, loading: false });
          return snapshot;
        } catch (error) {
          if (generation !== requestGeneration) return undefined;
          // A failed refresh does not establish that a known restriction ended.
          publish({ ...snapshot, error, loading: false });
          return snapshot;
        } finally {
          if (generation === requestGeneration) pending = undefined;
        }
      })();
      return pending;
    },
  };
}
